/**
 * audio-tracks.js — window.__audioTracks
 *
 * ОДИН владелец вопроса: «у входного файла несколько звуковых дорожек —
 * какая (или какие) доедут до выхода, и знает ли об этом человек?»
 *
 * До 2026-09-17 этот вопрос не принадлежал никому, и КАЖДЫЙ лейн отвечал на
 * него по-своему — молча и по-разному (замерено 17.09.2026 на одном и том же
 * файле rus/rus/eng, где английская дорожка помечена `default`):
 *
 *   | лейн                                    | что оставляет           |
 *   |-----------------------------------------|-------------------------|
 *   | FFmpeg без `-map` (video-compressor,     | ту, что помечена        |
 *   |   video-trim, mute-video, extract-audio) | `(default)` → ENG       |
 *   | `filter_complex [0:a]` (video-speed)     | ПЕРВУЮ → RUS            |
 *   | WebCodecsPipeline (аппаратный лейн)      | ПЕРВУЮ в moov → RUS     |
 *
 * То есть одна кнопка на одном файле давала РАЗНЫЙ ЯЗЫК в зависимости от
 * того, какой движок достался машине. Обращение #1665 (13.09.2026,
 * /video-compressor, Edge, файл `…1080p.BluRay.TRIPLE.-whip93`, 754 МБ,
 * 2 ч 15 мин кодирования): «не понимаю почему после сжатия язык поменялся на
 * английский».
 *
 * ⚠️⚠️ Ключевое: ПРАВИЛЬНОГО АВТОМАТИЧЕСКОГО ОТВЕТА НЕ СУЩЕСТВУЕТ. Плеер
 * человека выбрал дорожку по ЕГО настройке предпочитаемого языка — того, чего
 * в файле нет и чего FFmpeg не видит. Поэтому «брать первую» и «брать
 * default» одинаково ошибочны: в релизах, собранных с оригинального диска,
 * `default` стоит на английской, а дубляж дописан следом; в релизах,
 * собранных под русского зрителя, наоборот. Единственный верный ход —
 * СПРОСИТЬ, и спросить ДО работы, а не после двух часов кодирования.
 *
 * Предвыбор (когда человек ничего не трогает) — по убыванию доказательности:
 *   1. дорожка на языке интерфейса / `navigator.languages` (это и есть та
 *      настройка, по которой выбирал плеер);
 *   2. дорожка с флагом `(default)`;
 *   3. первая.
 *
 * Держит: разбор ВСЕХ дорожек из лога пробы, предвыбор, видимый выбор,
 * аргументы `-map` для FFmpeg-лейна и индекс для аппаратного, и счётчик
 * (`audio_tracks`), которого до сих пор не было — сколько вообще приходит
 * многодорожечных файлов, сайт не знал ни одного дня.
 */
(function () {
    'use strict';
    if (window.__audioTracks) return;

    /* ISO-639-1 → ISO-639-2. Только то, что реально встречается в тегах
       дорожек; неизвестный код сравнивается как есть. `deu/ger`, `fra/fre`,
       `zho/chi`, `ces/cze`, `nld/dut` — у ISO два кода на язык (T и B), и в
       файлах попадаются оба, поэтому значение — СПИСОК. */
    var L2TO3 = {
        en: ['eng'], ru: ['rus'], uk: ['ukr'], de: ['ger', 'deu'], fr: ['fre', 'fra'],
        es: ['spa'], pt: ['por'], it: ['ita'], ko: ['kor'], ja: ['jpn'], zh: ['chi', 'zho'],
        ar: ['ara'], id: ['ind'], tr: ['tur'], pl: ['pol'], nl: ['dut', 'nld'], cs: ['cze', 'ces'],
        sv: ['swe'], da: ['dan'], fi: ['fin'], no: ['nor'], he: ['heb'], hi: ['hin'],
        th: ['tha'], vi: ['vie'], ro: ['rum', 'ron'], hu: ['hun'], el: ['gre', 'ell'],
        bg: ['bul'], sr: ['srp'], hr: ['hrv'], sk: ['slo', 'slk'], be: ['bel'], kk: ['kaz']
    };
    /* Человекочитаемое имя дорожки. Намеренно НЕ через Intl.DisplayNames: он
       берёт 2-буквенные коды, а в дорожках лежат 3-буквенные, и на незнакомом
       коде бросает. Здесь — тихий возврат самого кода. */
    var NAMES3 = {
        eng: 'English', rus: 'Русский', ukr: 'Українська', ger: 'Deutsch', deu: 'Deutsch',
        fre: 'Français', fra: 'Français', spa: 'Español', por: 'Português', ita: 'Italiano',
        kor: '한국어', jpn: '日本語', chi: '中文', zho: '中文', ara: 'العربية', ind: 'Indonesia',
        tur: 'Türkçe', pol: 'Polski', dut: 'Nederlands', nld: 'Nederlands', cze: 'Čeština',
        ces: 'Čeština', swe: 'Svenska', dan: 'Dansk', fin: 'Suomi', nor: 'Norsk', heb: 'עברית',
        hin: 'हिन्दी', tha: 'ไทย', vie: 'Tiếng Việt', rum: 'Română', ron: 'Română',
        hun: 'Magyar', gre: 'Ελληνικά', ell: 'Ελληνικά', bul: 'Български', srp: 'Српски',
        hrv: 'Hrvatski', slo: 'Slovenčina', slk: 'Slovenčina', bel: 'Беларуская', kaz: 'Қазақша'
    };
    var CH_NAME = { 1: 'mono', 2: 'stereo', 3: '2.1', 4: 'quad', 6: '5.1', 8: '7.1' };

    function S(key, fallback) {
        var c = window.__commonStrings || {};
        return (c[key] != null && c[key] !== '') ? c[key] : fallback;
    }

    /**
     * parse — все звуковые дорожки из лога `ffmpeg -i`.
     *
     * Формы, которые обязаны разбираться (сняты с нашей же сборки 5.1.4):
     *   MP4: Stream #0:1[0x2](rus): Audio: aac (LC) (mp4a / 0x…), 44100 Hz, 5.1, fltp, 68 kb/s (default)
     *   MKV: Stream #0:3(eng): Audio: aac (LC), 44100 Hz, 5.1, fltp (default)
     *   без языка: Stream #0:1: Audio: mp3, 48000 Hz, stereo, fltp, 128 kb/s
     * Заголовок `title` лежит ПОСЛЕ строки дорожки, в блоке Metadata — и он и
     * есть то, что человек видел в плеере («Дубляж», «Авторский»).
     */
    function parse(log) {
        var out = [];
        if (!log) return out;
        var lines = String(log).split(/\r?\n/);
        var cur = null, inMeta = false;
        for (var i = 0; i < lines.length; i++) {
            var ln = lines[i];
            /* ⚠️ Лог пробы содержит НЕ ТОЛЬКО вход. `probeStreaming` гоняет
               `-i … -t 0 -f null -`, чтобы FFmpeg вышел нулём, и печатает
               блок `Output #0, null, to '-':` со своей строкой `Audio:`.
               Без этой границы у КАЖДОГО файла находилась лишняя
               дорожка (сторож показал 4 вместо 3 и 2 вместо 1) — то есть
               выбор рисовался бы и у однодорожечного файла, предлагая
               несуществующее. Второй `Input #` — тоже конец: нужен вход тула. */
            if (/^\s*Output\s+#/.test(ln)) break;
            var inp = ln.match(/^\s*Input\s+#(\d+)/);
            if (inp) { if (parseInt(inp[1], 10) > 0) break; cur = null; inMeta = false; continue; }
            var m = ln.match(/^\s*Stream\s+#(\d+):(\d+)(?:\[0x[0-9a-fA-F]+\])?(?:\(([A-Za-z\-]{2,8})\))?:\s*Audio:\s*([A-Za-z0-9_.\-]+)/);
            if (m) {
                var tail = ln.slice(ln.indexOf(m[0]) + m[0].length);
                var chm = tail.match(/\b(mono|stereo|5\.1\(side\)|5\.1|7\.1\(wide-side\)|7\.1\(wide\)|7\.1|quad|2\.1|(\d+)\s*channels)\b/i);
                var ch = null;
                if (chm) {
                    if (chm[2]) ch = parseInt(chm[2], 10);
                    else ch = { mono: 1, stereo: 2, '2.1': 3, quad: 4, '5.1': 6, '5.1(side)': 6, '7.1': 8, '7.1(wide)': 8, '7.1(wide-side)': 8 }[chm[1].toLowerCase()] || null;
                }
                var srm = tail.match(/(\d{4,6})\s*Hz/);
                var brm = tail.match(/(\d+)\s*kb\/s/);
                cur = {
                    input: parseInt(m[1], 10),
                    stream: parseInt(m[2], 10),
                    a: out.length,            /* ordinal among AUDIO streams — this is `-map 0:a:N` */
                    lang: (m[3] || '').toLowerCase(),
                    codec: (m[4] || '').toLowerCase(),
                    channels: ch,
                    sampleRate: srm ? parseInt(srm[1], 10) : null,
                    kbps: brm ? parseInt(brm[1], 10) : null,
                    def: /\(default\)/.test(tail),
                    forced: /\(forced\)/.test(tail),
                    title: ''
                };
                out.push(cur);
                inMeta = false;
                continue;
            }
            if (/^\s*Stream\s+#/.test(ln)) { cur = null; inMeta = false; continue; }
            if (!cur) continue;
            if (/^\s*Metadata:\s*$/.test(ln)) { inMeta = true; continue; }
            if (inMeta) {
                var t = ln.match(/^\s*title\s*:\s*(.+?)\s*$/i);
                if (t) { cur.title = t[1]; continue; }
                /* ⚠️ Не всякий ключ закрывает блок — DURATION / handler_name /
                   BPS живут здесь же. Закрывает только строка без отступа. */
                if (!/^\s{2,}/.test(ln)) inMeta = false;
            }
        }
        /* `und` / `unk` / `mis` — это «язык не указан», а не язык. Отличать
           обязательно, иначе предвыбор решит, что нашёл совпадение. */
        for (var k = 0; k < out.length; k++) {
            if (out[k].lang === 'und' || out[k].lang === 'unk' || out[k].lang === 'mis') out[k].lang = '';
        }
        return out;
    }

    /* Языковые предпочтения ЧЕЛОВЕКА по убыванию — та самая настройка, по
       которой его плеер и выбрал дорожку. Локаль страницы первая: на
       ru-домене она и есть заявленный язык. */
    function prefs() {
        var p = [];
        function push(code) {
            if (!code) return;
            var two = String(code).toLowerCase().split(/[-_]/)[0];
            var three = L2TO3[two] || [two];
            for (var i = 0; i < three.length; i++) if (p.indexOf(three[i]) === -1) p.push(three[i]);
        }
        push(window.__LOCALE);
        try { (navigator.languages || []).forEach(push); } catch (_) {}
        try { push(navigator.language); } catch (_) {}
        return p;
    }

    /**
     * pick — какую дорожку предложить, если человек ничего не выбирал.
     * Возвращает { a, why } — индекс среди звуковых дорожек и ПОЧЕМУ
     * (`lang` / `default` / `first`); «почему» уезжает в телеметрию, иначе
     * спор о качестве предвыбора через месяц будет спором о вкусах.
     */
    function pick(tracks, opts) {
        if (!tracks || !tracks.length) return { a: -1, why: 'none' };
        if (tracks.length === 1) return { a: 0, why: 'only' };
        var pf = (opts && opts.prefs) || prefs();
        for (var i = 0; i < pf.length; i++) {
            for (var j = 0; j < tracks.length; j++) {
                if (tracks[j].lang && tracks[j].lang === pf[i]) return { a: tracks[j].a, why: 'lang' };
            }
        }
        for (var k = 0; k < tracks.length; k++) if (tracks[k].def) return { a: tracks[k].a, why: 'default' };
        return { a: 0, why: 'first' };
    }

    function label(t) {
        var bits = [];
        var nm = t.lang ? (NAMES3[t.lang] || t.lang.toUpperCase()) : '';
        if (nm) bits.push(nm);
        if (t.title && t.title !== nm) bits.push(t.title);
        if (!bits.length) bits.push(S('audio_track_n', 'Track :n').replace(':n', (t.a + 1)));
        var extra = [];
        if (t.channels && CH_NAME[t.channels]) extra.push(CH_NAME[t.channels]);
        else if (t.channels) extra.push(t.channels + 'ch');
        if (t.codec) extra.push(t.codec.toUpperCase());
        var s = bits.join(' · ');
        if (extra.length) s += ' (' + extra.join(', ') + ')';
        return s;
    }

    /**
     * mapArgs — ЯВНЫЙ ответ в аргументах FFmpeg.
     *
     * Явность здесь не украшение: без `-map` ответ даёт автоподбор FFmpeg, и
     * он ДРУГОЙ, чем у аппаратного лейна того же тула. Один и тот же индекс,
     * записанный в обе стороны, — единственное, что делает лейны согласными.
     * `sel === 'all'` — оставить все дорожки (человек попросил явно).
     */
    function mapArgs(sel, opts) {
        var video = (opts && opts.video === false) ? [] : ['-map', '0:v:0'];
        if (sel === 'all') return video.concat(['-map', '0:a']);
        var a = (typeof sel === 'number') ? sel : (sel && typeof sel.a === 'number' ? sel.a : 0);
        if (a < 0) return video;
        return video.concat(['-map', '0:a:' + a]);
    }

    /**
     * mount — видимый выбор. При одной дорожке НЕ рисует ничего: почти все
     * файлы однодорожечные, и лишний орган там — шум, а не честность.
     * Возвращает контроллер даже когда ничего не нарисовал, чтобы у
     * вызывающего был один код-путь.
     */
    function mount(host, tracks, opts) {
        opts = opts || {};
        var state = { sel: pick(tracks, opts), all: false, el: null };

        function api() {
            return {
                tracks: tracks || [],
                count: (tracks || []).length,
                all: function () { return state.all; },
                index: function () { return state.all ? 'all' : state.sel.a; },
                why: function () { return state.all ? 'user-all' : state.sel.why; },
                mapArgs: function (o) { return mapArgs(state.all ? 'all' : state.sel.a, o); },
                destroy: function () { if (state.el && state.el.parentNode) state.el.parentNode.removeChild(state.el); }
            };
        }

        if (!host || !tracks || tracks.length < 2) return api();

        var wrap = document.createElement('div');
        wrap.className = 'atk-wrap';
        wrap.setAttribute('data-audio-tracks', String(tracks.length));
        var id = 'atk-sel-' + Math.random().toString(36).slice(2, 8);
        var lab = document.createElement('label');
        lab.className = 'atk-label';
        lab.setAttribute('for', id);
        lab.textContent = S('audio_track_label', 'Audio track');
        var sel = document.createElement('select');
        sel.className = 'atk-select';
        sel.id = id;
        for (var i = 0; i < tracks.length; i++) {
            var o = document.createElement('option');
            o.value = String(tracks[i].a);
            o.textContent = label(tracks[i]);
            sel.appendChild(o);
        }
        if (opts.allowAll !== false) {
            var oa = document.createElement('option');
            oa.value = 'all';
            oa.textContent = S('audio_track_all', 'All tracks (:n)').replace(':n', tracks.length);
            sel.appendChild(oa);
        }
        sel.value = String(state.sel.a);
        var note = document.createElement('div');
        note.className = 'atk-note';
        note.textContent = S('audio_track_note', 'This file has :n audio tracks — the result keeps the one selected here.')
            .replace(':n', tracks.length);
        wrap.appendChild(lab);
        wrap.appendChild(sel);
        wrap.appendChild(note);
        host.appendChild(wrap);
        state.el = wrap;

        sel.addEventListener('change', function () {
            state.all = (sel.value === 'all');
            if (!state.all) state.sel = { a: parseInt(sel.value, 10), why: 'user' };
            try {
                window.__ae && window.__ae('feature_use', {
                    slug: opts.slug || '', f: 'audio_track_changed',
                    to: sel.value, n: tracks.length
                });
            } catch (_) {}
            if (typeof opts.onChange === 'function') opts.onChange(api());
        });

        return api();
    }

    /**
     * report — единственный прибор класса. До него сайт не знал, сколько
     * многодорожечных файлов к нему приходит: ни одно событие не несло число
     * дорожек, поэтому «часто ли это» было предметом догадок.
     */
    function report(slug, tracks, chosen) {
        try {
            if (!window.__ae || !tracks) return;
            var defIdx = -1;
            for (var i = 0; i < tracks.length; i++) if (tracks[i].def) defIdx = i;
            window.__ae('feature_use', {
                slug: slug || '', f: 'audio_tracks',
                n: tracks.length,
                langs: tracks.map(function (t) { return t.lang || '?'; }).join(',').slice(0, 60),
                picked: chosen ? chosen.a : -1,
                why: chosen ? chosen.why : 'none',
                def: defIdx
            });
        } catch (_) {}
    }

    window.__audioTracks = {
        parse: parse, pick: pick, mapArgs: mapArgs, mount: mount,
        report: report, label: label, prefs: prefs, _names: NAMES3
    };
})();
